/* @flow */
import { OrderedMap, fromJS } from 'immutable';
import { combineReducers } from 'redux-immutable';
import createDebugger from 'debug';
import type { Action } from 'trustar/utils/redux-types.js';

const debug = createDebugger('app:modules:companies:stores'); // eslint-disable-line no-unused-vars

import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  SAVE,
  SAVE_SUCCESS,
  SAVE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  SET_DISPLAY_OPTIONS,
  FETCH_QUOTA,
  FETCH_QUOTA_SUCCESS,
  FETCH_QUOTA_FAILURE,
  SAVE_QUOTA,
  SAVE_QUOTA_SUCCESS,
  SAVE_QUOTA_FAILURE,
  RESET_QUOTA,
  RESET_QUOTA_SUCCESS,
  RESET_QUOTA_FAILURE,
} from './action-types.js';
import { Company, Options} from './types.js';
import type { CompanyState, OptionsRecord } from './types.js';

const fromJSON = (rawCompany) => {
  const parsed = fromJS(rawCompany);
  return Company(parsed);
};

const listToOrderedMap = (list) => {
  return fromJS(list)
    .toOrderedMap()
    .mapKeys((k, v) => v.get('guid'));
};

export const companies = (state: CompanyState = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_SUCCESS: {
    return listToOrderedMap(payload.map(fromJSON));
  }
  case DELETE_SUCCESS:
    return state.delete(payload); // The ID to be deleted
  default:
    return state;
  }
};

export const loading = (state: boolean = false, action: Action<*>) => {
  switch (action.type) {
  case FETCH:
  case SAVE:
  case DELETE:
    return true;
  case FETCH_SUCCESS:
  case SAVE_SUCCESS:
  case DELETE_SUCCESS:
  case FETCH_FAILURE:
  case SAVE_FAILURE:
  case DELETE_FAILURE:
    return false;
  default:
    return state;
  }
};

export const options = (state: OptionsRecord = Options(), action: Action<*>): OptionsRecord => {
  const { type, payload } = action;

  switch (type) {
  case SET_DISPLAY_OPTIONS:
    return state.merge(payload);
  case FETCH_SUCCESS:
    return state.merge(fromJS(action.meta));
  default:
    return state;
  }
};

/**
 * Whether any quotas are loading. We don't currently ever display more than one
 * at once so this single boolean will suffice to track this state.
 */
const loadingQuotas = (state: boolean = false, action: Action<*>) => {
  switch (action.type) {
  case FETCH_QUOTA:
  case SAVE_QUOTA:
  case RESET_QUOTA:
    return true;
  case FETCH_QUOTA_SUCCESS:
  case SAVE_QUOTA_SUCCESS:
  case RESET_QUOTA_SUCCESS:
  case FETCH_QUOTA_FAILURE:
  case SAVE_QUOTA_FAILURE:
  case RESET_QUOTA_FAILURE:
    return false;
  default:
    return state;
  }
};

/**
 * Whether companies have been fully loaded. At times, the companies store will
 * hold shallow projections of companies (only guid + name) - so a new request
 * needs to be made in order to grab the entire objects.
 */
export const fullyLoaded = (state: boolean = false, action: Action<any>) => {
  const { type, payload } = action;

  switch(action.type) {
  case FETCH_SUCCESS:
    const x = listToOrderedMap(payload.map(fromJSON));
    return x.filter(x => x.get('id') || x.get('primaryEmail')).size != 0
  case FETCH_FAILURE:
    return true;
  default:
    return state;
  }
};

export default combineReducers({
  companies,
  loading,
  options,
  loadingQuotas,
  fullyLoaded,
});



// WEBPACK FOOTER //
// ./src/app/modules/companies/stores.js